<template>
  <div class="content">
    <h2 class="font-light">Course Index</h2>
    <server-error v-bind:error="error.serverError"></server-error>
    <div class="row">
      <search-field
        v-bind:search="filters.search"
        @handleSearch="handleSearch"
      ></search-field>
      <div class="col-4">
        <div class="form-group u-inline-flex u-pull-right">
          <filter-dropdown
            v-bind:options="filterOptions"
            v-on:handle-filter="handleFilter"
          ></filter-dropdown>
          <!-- New course button -->
          <router-link
            v-if="permissions.includes('courses.add_course')"
            :to="{ name: 'course-create' }"
          >
            <button id="addButton" class="btn-info action-button">
              <span class="icon">
                <i
                  class="fa-wrapper fa fa-plus-circle normal-icon"
                  aria-hidden="true"
                ></i>
              </span>
              <span>New</span>
            </button>
          </router-link>
        </div>
      </div>
      <filter-tags v-bind:filterOptions="filterOptions"></filter-tags>
    </div>
    <!-- Course table -->
    <table class="table">
      <thead>
        <tr>
          <th></th>
          <th>Course</th>
          <th>Candidate</th>
          <th>Status</th>
          <th>Start Date</th>
          <th>End Date</th>
          <th>Updated</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="courseList.length == 0">
          <td colspan="8">
            <no-results-found
              title="No Results Found"
              message="Maybe change the search and filters and try again?"
              ></no-results-found>
          </td>
        </tr>
        <tr
          v-else
          v-for="course in courseList"
          :key="course.id"
        >
          <td>
            <button
              v-if="permissions.includes('courses.view_course')"
              class="btn-small u-center btn-info"
            >
              <router-link
                v-bind:to="{ name: 'course-detail', params: { id: course.id }}"
              >
                View
              </router-link>
            </button>
          </td>
          <td>{{ course.name }}</td>
          <td>{{ course.candidate.full_name }}</td>
          <td>
            <div
              v-if="course.is_complete"
              class="tag tag--white tooltip tag--large"
              data-tooltip="Complete"
            >
              <i
                class="fa fa-wrapper fa-check-circle"
                style="color: var(--cirrus-success);"
              >
              </i>
            </div>
            <inactive-icon v-bind:is_active="course.is_active"></inactive-icon>
          </td>
          <td>{{ formatDate(course.start_date) }}</td>
          <td>{{ formatDate(course.end_date) }}</td>
          <td>{{ formatDateTime(course.last_updated) }}</td>
        </tr>
      </tbody>
    </table>
    <div class="row">
      <pagination-links
        v-if="pages.courseList"
        v-bind:pages="pages.courseList"
        @handlePage="handlePage"
      ></pagination-links>
      <page-size-selector
        v-on:handle-page-size="handlePageSize()"
      ></page-size-selector>
    </div>
  </div>
</template>
<script>
import { mapGetters } from "vuex";
import ServerError from "@/components/ServerError";
import SearchField from "@/components/SearchField";
import FilterDropdown from "@/components/FilterDropdown";
import FilterTags from "@/components/FilterTags";
import InactiveIcon from "@/components/InactiveIcon";
import NoResultsFound from "@/components/PlaceholderNoResults";
import PaginationLinks from "@/components/Pagination";
import PageSizeSelector from "@/components/PageSizeSelector";

import { debounce} from "@/functions/debounce.function";
import { DateFormatter } from "@/functions/dateFormat.function";
import { DateTimeFormatter } from "@/functions/dateTimeFormat.function";
import { SetupFilters } from "@/services/filters.service";
import { GetSelectedFilters } from "@/services/filters.service";
import { HandleAllFilters } from "@/services/filters.service";
import { QueryHasChanged } from "@/services/filters.service";

export default {
  name: "course-index",
  components: {
    NoResultsFound,
    SearchField,
    InactiveIcon,
    FilterDropdown,
    FilterTags,
    ServerError,
    PaginationLinks,
    PageSizeSelector,
  },
  data: function() {
    return {
      filterOptions: {
        is_active: [
          { name: "True", value: "true", selected: false },
          { name: "False", value: "false", selected: false }
        ],
        is_complete: [
          { name: "True", value: "true", selected: false },
          { name: "False", value: "false", selected: false }
        ],
        name: [],
      }
    }
  },
  computed: {
    ...mapGetters("courses", ["search", "filters", "pages", "courseList", "courseTemplates"]),
    ...mapGetters("core", ["error"]),
    ...mapGetters("auth", ["permissions",]),
  },
  watch: {
    search: debounce(function() {
      this.$store.dispatch("courses/getCourseList");
      this.$router.replace({
        name: "course-index",
        query: { ...this.filter }
      });
    }, 300),
    courseTemplates: function() {
      this.setupCourseFilter();
    }
  },
  methods: {
    formatDate(val) {
      return DateFormatter(val);
    },
    formatDateTime(val) {
      return DateTimeFormatter(val);
    },
    setupCourseFilter() {
      let stack = [];
      for (let course of this.courseTemplates.filter(i => i.is_active == true)) {
        let filter = {};
        filter["name"] = course.name;
        filter["value"] = course.name;
        filter["selected"] = false;
        stack.push(filter);
      };
      this.filterOptions["name"] = stack;
    },
    handleSearch(searchVal) {
      this.$store.commit("courses/filters", { search: searchVal });
      this.$store.dispatch("courses/getCoursList");
      if (QueryHasChanged(this.$route.query, this.filters)) {
        this.$router.replace({
          name: "course-index",
          query: { ...this.filters }
        });
      };
    },
    handlePage(newPage) {
      if (newPage == 1) {
        newPage == "";
      }
      this.$store.commit("courses/filters", { page: newPage });
      this.$store.dispatch("courses/getCourseList");
      if (QueryHasChanged(this.$route.query, this.filters)) {
        this.$router.replace({
          name: "course-index",
          query: { ...this.filters }
        });
      }
    },
    handlePageSize() {
      // Refresh the array by going to page 1 when the pageSize is changed
      this.handlePage(1);
    },
    handleFilter(payload) {
      HandleAllFilters(this.filters, this.filterOptions, payload);
      // Reset the page to 0 as we don't know how many pages to expect!
      payload.page = "";
      this.$store.commit("courses/filters", payload);
      this.$store.dispatch("courses/getCourseList");
      this.$router.replace({
        name: "course-index",
        query: { ...this.filters }
      });
    },
  },
  mounted() {
    // Re-hydrate the filter with either the URL query param or values from the store
    SetupFilters(this, "courses/filters", "course-index");
    this.setupCourseFilter();
    for (let key in this.$route.query) {
      GetSelectedFilters(this.filterOptions[key], this.$route.query[key]);
    }
    // Default to 'is_active == true' is no other filters selected
    if (Object.keys(this.filters).length == 0) {
      this.handleFilter({ is_active: "true" });
    }
    this.$store.commit("core/clearErrors");
    this.$store.dispatch("courses/getCourseList");
    this.$store.dispatch("courses/getCourseTemplates");
  }
};
</script>
